import './Persons.scss';
import Table from '../../components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import magnifier from 'assets/images/icons/magnifier.svg';
import plus from 'assets/images/icons/plus.svg';
import eyeIcon from 'assets/images/icons/eye.svg';
import Input from '../../components/Input/Input';
import { Button } from '../../components/Button/Button';
import { Link, useNavigate } from 'react-router-dom';
import { Person } from 'services/person/person.types';
import usePersonsVM from './usePersonsVM';
import penIcon from 'assets/images/icons/pen.svg';
import { statusMap } from 'utils/enumFormatter';
import { FaFilter } from 'react-icons/fa';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist/datalist.slice';
import { Loader } from 'components/Loader/Loader';
import { IoWarning } from 'react-icons/io5';

export type PersonsPageProps = {};

const columnHelper = createColumnHelper<Person>();

export const Persons: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const { datalists } = useSelector(getDatalists);
    const { persons, search, setSearch, filter, setFilter, filterIsOpen, setFilterIsOpen, isGetPersonsLoading, users, personsFiltered } =
        usePersonsVM();

    const tableColumns = [
        columnHelper.accessor('disorders.mdph', {
            header: '',
            cell: (info) => <i>{info.getValue() === 'no' ? <IoWarning size={24} title="MDPH non reconnu" /> : null}</i>,
            footer: (info) => info.column.id,
            size: 10,
            maxSize: 10,
            minSize: 10
        }),
        columnHelper.accessor('fullname', {
            header: 'Nom complet',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('status', {
            header: 'Statut',
            cell: (info) => <i>{statusMap[info.getValue() || '']?.label || info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('responsible_person', {
            header: 'APPV',
            cell: (info) => <i>{info.getValue()?.fullname || 'Aucun'}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('departments', {
            header: 'Dpt',
            cell: (info) => (
                <i>
                    {info
                        .getValue()
                        ?.map((department: string) => datalists?.departments?.[department] || '')
                        .join(', ')}
                </i>
            ),
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('first_contact_date', {
            header: "Date d'entrée",
            cell: (info) => <i>{moment(info.row.original.first_contact_date || info.row.original.created_at).format('DD/MM/YYYY')}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('updated_at', {
            header: 'Dernière actualisation',
            cell: (info) => <i>{moment(info.getValue()).format('DD/MM/YYYY')}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.display({
            header: 'Actions',
            cell: ({ row: { original: currRow } }) => {
                return (
                    <span style={{ display: 'flex', columnGap: 12 }}>
                        <Link to={`/app/admin/persons/${currRow.id}`}>
                            <img src={eyeIcon} alt="eye-icon" />
                        </Link>
                        <Link to={`/app/admin/persons/${currRow.id}/edit`}>
                            <img src={penIcon} alt="pen-icon" />
                        </Link>
                    </span>
                );
            }
        })
    ];

    return (
        <main id="persons">
            <div className="top">
                <div className="search-container">
                    <Input
                        placeholder="Chercher un dossier"
                        name="search-person"
                        className="search-person search-bar"
                        icon={<img src={magnifier} alt="" />}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search ? search : ''}
                    />
                    <Button
                        variant="light"
                        className="filter-button"
                        icon={<FaFilter />}
                        iconBefore
                        chip={filter.departments.length + filter.statuses.length + filter.appvs.length}
                        callback={() => setFilterIsOpen(!filterIsOpen)}
                    />
                </div>
                <Button
                    label="Soutenir une personne"
                    variant="light"
                    icon={<img src={plus} alt="" />}
                    iconBefore
                    callback={() => navigate('/app/admin/persons/new')}
                />
            </div>
            {filterIsOpen && (
                <ContentBlock title="Filtrer par">
                    <div className="filter-container">
                        <div className="form">
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Département(s)</label>
                                    <MultiSelect
                                        name="departments"
                                        placeholder="Selectionner des départements"
                                        values={filter.departments}
                                        labelKey="label"
                                        valueKey="value"
                                        update={(e: { label: string; value: string }[]) => {
                                            setFilter({
                                                ...filter,
                                                departments: e
                                            });
                                        }}
                                        options={Object.entries(datalists?.departments || {}).map(([key, val]) => ({
                                            label: val,
                                            value: key
                                        }))}
                                        colors={[]}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Statut(s)</label>
                                    <MultiSelect
                                        name="statuses"
                                        placeholder="Selectionner des statuts"
                                        values={filter.statuses}
                                        labelKey="label"
                                        valueKey="value"
                                        update={(e: { label: string; value: string }[]) => {
                                            setFilter({
                                                ...filter,
                                                statuses: e
                                            });
                                        }}
                                        options={Object.entries(statusMap ?? {}).map(([key, val]) => ({
                                            label: val.label,
                                            value: key
                                        }))}
                                        colors={[]}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>APPV(s)</label>
                                    <MultiSelect
                                        name="appvs"
                                        placeholder="Selectionner des APPVs"
                                        values={filter.appvs}
                                        labelKey="label"
                                        valueKey="value"
                                        update={(e: { label: string; value: string }[]) => {
                                            setFilter({
                                                ...filter,
                                                appvs: e
                                            });
                                        }}
                                        options={users.map((user) => ({
                                            label: user.fullname,
                                            value: user.id
                                        }))}
                                        colors={[]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ContentBlock>
            )}
            {isGetPersonsLoading ? (
                <Loader />
            ) : personsFiltered && personsFiltered.length > 0 ? (
                <Table
                    pageSize={12}
                    data={personsFiltered}
                    columns={tableColumns}
                    columnHelper={columnHelper}
                    search={search}
                    className="table-card"
                    isLoading={isGetPersonsLoading}
                    searchableFields={['reference', 'fullname', 'department', 'status', 'lastname', 'firstname', 'phone', 'email']}
                />
            ) : filter.departments.length > 0 || filter.statuses.length > 0 || filter.appvs.length > 0 || search.length > 0 ? (
                <div className="no-result">
                    <label>Aucun résultat trouvé</label>
                </div>
            ) : persons && persons.length > 0 ? (
                <Table
                    pageSize={12}
                    data={persons}
                    columns={tableColumns}
                    columnHelper={columnHelper}
                    search={search}
                    className="table-card"
                    isLoading={isGetPersonsLoading}
                    searchableFields={['reference', 'fullname', 'department', 'status', 'lastname', 'firstname', 'phone', 'email']}
                />
            ) : (
                <div className="no-result">
                    <label>Aucune personne enregistrée</label>
                </div>
            )}
        </main>
    );
};
